import { Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, map, takeUntil } from 'rxjs';
import { Page } from '../constants';
import { TranslationFromJsonService } from '@shared/feature/translate';

@Directive({
    selector: '[featureTabsTabsTitle]',
    standalone: false
})
export class TabsTitleDirective implements OnInit, OnDestroy {
  @Input() prefix = '| ';

  private ngUnsubscribe$ = new Subject<void>();

  constructor(private router: Router, private translationFromJsonService: TranslationFromJsonService) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((x) => {
          setTimeout(() => window.scroll(0, 0), 0);

          return x.url;
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((currentUrl) => {
        this.setTabTitle(currentUrl);
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private setTabTitle(url: string): void {
    let hasMatch = false;

    const uuidPattern = '[0-9a-fA-F]{8}(-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}';

    const keyValuePairs = {
      [`^${Page.Url.Dashboard}$`]: Page.Title.Dashboard,
      [`^${Page.Url.Customers}$`]: this.translationFromJsonService.instant(Page.Title.Customers),
      [`^${Page.Url.Users}$`]: this.translationFromJsonService.instant(Page.Title.Users),
      [`^${Page.Url.EvidenceLabeling}$`]: Page.Title.EvidenceLabeling,
      [`^${Page.Url.AllFiles}$`]: this.translationFromJsonService.instant(Page.Title.AllFiles),
      [`^${Page.Url.VideoBrowser}$`]: Page.Title.VideoBrowser,
      [`^${Page.Url.PendingRemoval}$`]: this.translationFromJsonService.instant(Page.Title.PendingRemoval),
      [`^${Page.Url.UploadFiles}$`]: this.translationFromJsonService.instant(Page.Title.UploadFiles),
      [`^${Page.Url.Folders}$`]: this.translationFromJsonService.instant(Page.Title.Folders),
      [`^${Page.Url.SharedWithMe.replace('?', '\\?')}$`]: this.translationFromJsonService.instant(Page.Title.SharedWithMe),
      [`^${Page.Url.SharedByMe}$`]: this.translationFromJsonService.instant(Page.Title.SharedByMe),
      [`^${Page.Url.Uploader}$`]: this.translationFromJsonService.instant(Page.Title.Uploader),
      [`^${Page.Url.AllFiles}/${uuidPattern}(\\?isFullScreen=True)?$`]: this.translationFromJsonService.instant(Page.Title.FileDetails),
      [`^${Page.Url.AllFiles}/${uuidPattern}${Page.Url.SelfRedaction}$`]: this.translationFromJsonService.instant(Page.Title.SelfRedaction),
      [`^${Page.Url.Folders}/${uuidPattern}$`]: this.translationFromJsonService.instant(Page.Title.FolderDetails),
      [`^${Page.Url.Users}/${uuidPattern}$`]: Page.Title.UserDashboard,
      [`^${Page.Url.B2CLocked}$`]: Page.Title.Locked,
      [`^${Page.Url.B2CForbiden}$`]: Page.Title.Forbidden,
      [`^${Page.Url.Error}$`]: Page.Title.Error404,
      [`^${Page.Url.Error}/403(\\?backUrl=.+)?$`]: Page.Title.Error403,
      [`^${Page.Url.Error}/InternalServer$`]: Page.Title.Error500,
      [`^${Page.Url.Locked}$`]: Page.Title.Locked,
      [`^${Page.Url.Login}$`]: Page.Title.Login,
      [`^${Page.Url.Register}(\\?.+)?$`]: Page.Title.Register,
    };

    for (const [pattern, title] of Object.entries(keyValuePairs)) {
      if (new RegExp(pattern).test(url)) {
        document.title = this.prefix.concat(title);
        hasMatch = true;
        break;
      }
    }

    if (!hasMatch) {
      document.title = this.translationFromJsonService.instant('tenant-page-title');
    }
  }
}
